import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, endOfMonth } from 'date-fns';
import { hr } from 'date-fns/locale';
import { t, formatCroatianMonth } from '../../i18n/languages';
import { useYearStatus } from '../../contexts/YearStatusContext';

function MonthNavigation({
  currentMonth,
  setCurrentMonth,
  currentLanguage
}) {
  const { getMinNavigableYear, getMaxNavigableYear, loading: yearStatusLoading } = useYearStatus();

  // Define minimum and maximum allowed dates based on year status
  const minYear = yearStatusLoading ? 2025 : getMinNavigableYear();
  const maxYear = yearStatusLoading ? new Date().getFullYear() : getMaxNavigableYear();

  const minDate = new Date(minYear, 0, 1); // January of min year
  const maxDate = endOfMonth(new Date(maxYear, 1, 1)); // end of February of max year

  const isAtMinimum = currentMonth.getFullYear() === minYear && currentMonth.getMonth() === 0;
  const isAtMaximum = currentMonth.getFullYear() === maxYear && currentMonth.getMonth() === 1;

  const handlePreviousMonth = () => {
    const previousMonth = subMonths(currentMonth, 1);
    if (previousMonth >= minDate) {
      setCurrentMonth(previousMonth);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    if (nextMonth <= maxDate) {
      setCurrentMonth(nextMonth);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={handlePreviousMonth}
          disabled={isAtMinimum}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: isAtMinimum ? '#f3f4f6' : 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: isAtMinimum ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: isAtMinimum ? '#9ca3af' : '#374151',
            opacity: isAtMinimum ? 0.5 : 1
          }}
          title={isAtMinimum ?
            (t('earliestAvailableMonth', {}, currentLanguage)) :
            t('previousMonth', {}, currentLanguage)
          }
        >
          <ChevronLeft size={16} />
          {t('previousMonth', {}, currentLanguage)}
        </button>

        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
          {formatCroatianMonth(currentMonth, currentLanguage) ||
            format(currentMonth, t('monthYear', {}, currentLanguage), {
              locale: currentLanguage === 'hr' ? hr : undefined
            })}
        </h2>

        <button
          onClick={handleNextMonth}
          disabled={isAtMaximum}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: isAtMaximum ? '#f3f4f6' : 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: isAtMaximum ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: isAtMaximum ? '#9ca3af' : '#374151',
            opacity: isAtMaximum ? 0.5 : 1
          }}
          title={isAtMaximum ?
            t('latestAvailableMonth', {}, currentLanguage) :
            t('nextMonth', {}, currentLanguage)
          }
        >
          {t('nextMonth', {}, currentLanguage)}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

export default MonthNavigation;
